//PAGE
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import PageContent from '../layouts/PageContent';
//API
import axios, { AxiosError } from 'axios';
import { oktaClients } from '../../util/Okta';
import settings from '../../settings.json';
//COMPONENTS
import { Button, Divider, message, Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import {
    FileImageOutlined,
    FileSearchOutlined,
    ReloadOutlined,
    SendOutlined,
} from '@ant-design/icons';
//SEARCH PARAM
import { useSearchParams } from 'react-router-dom';
import { FileConversionStatus, FileScanStatus, SftJobStatus, UploadStatus } from '../../types/FileStatuses';
import { CurrentRequestStatusElements } from './CurrentRequestStatusElements';
import { FileStatusColumn, UploadStatusColumn } from './FileStatusColumns';

//get api prefix
const settingEnv = (settings as any)[window.location.host] || settings.default;
const apiPrefix = settingEnv.api_prefixes.file_upload;
const { useContinuousAuth } = oktaClients.file_upload;

export function FileUploadDetails () {
    return (
        <PageContent
            authenticated={true}
            bodyColor='#fff'
            expanded={true}
            clientKey='file_upload'
            clientName='File Upload'
        >
            <AuthedFileUploadDetails />
        </PageContent>
    );
}

//styled components
const FileUploadBody = styled.div`
    margin: 0px 0px 40px 0px;
    padding: 10px;
    max-width: 770px;

    .ant-table-column-sort {
        background: #fff !important; //override default ANTD Table Column Sort background color
    }
`;
const FileDetailsReminder = styled.p`
    font-size: 10px;
    margin: 5px 0px;
`;

const TableRowDate = styled.p`
    margin: -10px 0px;
    font-size: 12px;
`;

const FileNameText = styled.span`
    display: inline-block;
    text-overflow: ellipsis;
    overflow: hidden;
    whiteSpace: nowrap;
    width: 100%;
    max-width: 470px;

    // ensure file name fills available space when the user is not in a condensed view
    @media (min-width: 900px) {
        min-width: 470px;
    }
`;

type ListUploadsRequest = {
    caseId?: string;
    customerId?: string;
};

type FileEntry = {
    name: string;
    fileId: string;
    scanStatus: FileScanStatus;
    conversionStatus: FileConversionStatus;

    sftJobStatus?: SftJobStatus;
    availableForDownload?: boolean;
};

export type ListUploadEntry = {
    caseId: string;
    uploadId: string;
    createdAt: number;
    expiresAt: number;
    status: UploadStatus;
    files?: FileEntry[];
};

export type FlattenedListUploadEntry = Omit<ListUploadEntry, 'files'> & Partial<FileEntry>;

type ListUploadsResponse = ListUploadEntry[];

type CreateUploadRequest = {
    uploadId?: string; // resend a request
    caseId?: string;
};

type CreateUploadResponse = {
    uploadId: string;
};

// Flatten the fileDetails data so each file associated with a request displays in it's own Table Row
function flattenFileDetails (fileDetails: ListUploadsResponse): FlattenedListUploadEntry[] {
    const flattenedData: FlattenedListUploadEntry[] = [];

    fileDetails.forEach((uploadEntry) => {
        const { files, ...rest } = uploadEntry;
        if (files && files.length > 0) {
            files.forEach((file) => {
                flattenedData.push({
                    ...rest,
                    ...file,
                });
            });
        } else if (!files || files.length === 0) {
            flattenedData.push(rest);
        }
    });

    return flattenedData;
}

// File Upload Details Page Content
function AuthedFileUploadDetails () {
    // url params
    const [searchParams] = useSearchParams();
    const caseId = searchParams.get('caseId') || undefined;
    const caseStatus = searchParams.get('status');
    const customerId = searchParams.get('customerId') || undefined;
    // file details
    const [flattenedFileDetails, setFlattenedFileDetails] = useState<FlattenedListUploadEntry[]>([]);
    // loading
    const [createLoading, setCreateLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);

    const { getHeaders } = useContinuousAuth();

    const currentRequest: {
        uploadId: string;
        status: UploadStatus;
    } | undefined = useMemo(() => {
        const currentTime = Date.now();
        const latestRequest = flattenedFileDetails[0];
        if (latestRequest && latestRequest.expiresAt > currentTime) {
            return {
                uploadId: latestRequest.uploadId,
                status: latestRequest.status,
            };
        }
    }, [flattenedFileDetails]);

    useEffect(() => {
        if (customerId) {
            listFileDetails({ caseId, customerId });
        } else if (caseId) {
            listFileDetails({ caseId });
        }
    }, [caseId, customerId]);

    const createUpload = async (
        caseId: string,
        uploadId?: string,
    ) => {
        setCreateLoading(true);
        setLoading(true);
        const headers = await getHeaders();
        let payload: CreateUploadRequest = {};
        //set uploadId if it exists
        if (uploadId) {
            payload.uploadId = uploadId;
        }
        payload.caseId = caseId;

        try {
            await axios.post<CreateUploadResponse>(`${apiPrefix}/api/upload`, payload, { headers });
            await listFileDetails({ caseId });
        } catch (e: unknown) {
            if (e instanceof AxiosError && e.response) {
                const error = e.response.data.error;
                if (error.includes('Unable to send file upload link') || error.includes('Error calling webhook')) {
                    message.error('There was a problem creating your Request, email cannot be sent');
                } else if (error.includes('Case status is closed') || error.includes('status')) {
                    message.error('There was a problem creating your Request, status is invalid');
                } else if (error.includes('Invalid case ID') || error.includes('Error getting case')) {
                    message.error('There was a problem creating your Request, case does not exist');
                } else {
                    message.error('There was a problem creating your Request');
                }
                console.error('Request creation error', error);
            }
        } finally {
            setLoading(false);
            setCreateLoading(false);
        }
    };

    //list file details
    const listFileDetails = async (
        {
            caseId,
            customerId,
        }: {
            caseId?: string;
            customerId?: string;
        }) => {
        setLoading(true);
        let params: ListUploadsRequest = {};
        if (caseId) {
            params = { caseId };
        } else if (customerId) {
            params = { customerId };
        }

        const headers = await getHeaders();
        try {
            const { data } = await axios.get<ListUploadsResponse>(`${apiPrefix}/api/uploads`, { headers, params });

            if (data.length > 0) {
                const currentTime = new Date().getTime();
                // mark any expired uploads in requested, uploading, or scanning status as expired
                const processedData: ListUploadEntry[] = data.map((record) => {
                    if (currentTime > Number(record.expiresAt)
                        && ([UploadStatus.Requested, UploadStatus.Uploading, UploadStatus.Scanning].includes(record.status))
                    ) {
                        return {
                            ...record,
                            status: UploadStatus.Expired,
                        };
                    } 

                    return record;
                }).sort((record1, record2) => record2.createdAt - record1.createdAt);

                // Flatten the data for the table
                const flattenedData = flattenFileDetails(processedData);
                setFlattenedFileDetails(flattenedData);
            }
        } catch (e: unknown) {
            if (e instanceof Error) {
                message.error('There was a problem retrieving file details');
                console.error('list files error', e.message);
            }
        } finally {
            setLoading(false);
            setRefreshLoading(false);
        }
    };

    // Table Columns
    const tableColumns: ColumnsType<FlattenedListUploadEntry> = useMemo(() => [
        {
            title: 'Download',
            dataIndex: 'uploadId',
            width: 175,
            render (_value: string, record: FlattenedListUploadEntry, _index: number) {
                // customize what button displays based on status
                if (!record.fileId) {
                    // Upload does not have files
                    return <UploadStatusColumn
                        record={record}
                        requestLoading={createLoading}
                        resendRequest={createUpload}
                    />
                }
                return <FileStatusColumn record={record} /> 
            },
        },
        {
            title: 'File Name',
            dataIndex: 'name',
            render (value: string, _record: FlattenedListUploadEntry, _index: number) {
                return (
                    <FileNameText>
                        {value}
                    </FileNameText>
                );
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            render (value: string, _record: FlattenedListUploadEntry, _index: number) {
                const createdString = new Date(+value);
                const createdDate = createdString.toString().slice(3, 21);
                return (
                    <TableRowDate>
                        {createdDate}
                    </TableRowDate>
                );
            },
        },
    ], [createLoading]);

    //page content
    return (
        <FileUploadBody>
            {/* Button Component */}
            {((!currentRequest || [UploadStatus.Completed, UploadStatus.Failed, UploadStatus.Scanning, UploadStatus.Expired].includes(currentRequest.status)) &&
                caseStatus === 'open' && caseId) &&
                <Button
                    type='primary'
                    icon={<SendOutlined />}
                    loading={createLoading}
                    onClick={() => {
                        createUpload(caseId);
                    }}
                    style={{ margin: '0px' }}
                >
                    File Upload Request
                </Button>
            }
            {/* Request Details Component */}
            {currentRequest &&
                <>
                    <Divider
                        orientation='left'
                        style={{
                            color: '#1890ff',
                            margin: '10px 0px',
                        }}
                    >
                        <FileSearchOutlined style={{ marginRight: '5px' }} />
                        Request Details
                    </Divider>
                    {CurrentRequestStatusElements[currentRequest.status]}
                </>
            }
            {/* File Details Component*/}
            <Divider
                orientation='left'
                style={{
                    color: '#1890ff',
                    margin: '15px 0px 10px 0px',
                }}
            >
                <FileImageOutlined style={{ marginRight: '5px' }} />
                File Details
            </Divider>
            <Table
                columns={tableColumns}
                dataSource={flattenedFileDetails || undefined}
                loading={loading}
                showHeader={false}
                rowKey={record => `${record.uploadId}-${record.fileId || record.name || ''}`}
                size='small'
                style={{
                    backgroundColor: '#fff',
                }}
                pagination={{
                    pageSize: 5,
                    position: ['bottomCenter'],
                }}
            />
            {/* Refresh List Component*/}
            <FileDetailsReminder>
                Not Seeing the File you are looking for?
            </FileDetailsReminder>
            <Button
                type='primary'
                loading={refreshLoading}
                onClick={() => {
                    setRefreshLoading(true);
                    listFileDetails({ caseId, customerId });
                }}
                icon={<ReloadOutlined />}
            >
                Refresh
            </Button>
        </FileUploadBody>
    );
}
